import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Input, Picker } from 'antd-mobile';
import { DownOutline } from 'antd-mobile-icons';
import type { PickerValue } from 'antd-mobile/es/components/picker';
import { Header } from '../components/Header';
import { ScanInput } from '../components/ScanInput';
import { Pda, useScanResult } from '../components/Pda';
import { t } from '../../../i18n';
import { parseException } from '../../../utils/newExceptionUtils';
import { playScanFailedAoSound } from '../../../utils/scanUtils';
import {
    ScanResultEnvelope,
    ScanResultPrompt,
    useScanResultPrompt,
    ClientError,
    buildClientError,
    getEnvelopeFailedReason,
} from '../scanResult';
import {
    GET_BAG_RESHIP_FLOW_OPTIONS,
    GetBagReshipFlowOptionsResponse,
    ReshipCarrierType,
} from './graphql/bagReshipFlowOptions';
import {
    HANDLE_BAG_RESHIP_SCAN_EVENT,
    HandleBagReshipScanEventResponse,
    HandleBagReshipScanEventVariables,
    BagReshipScanResultType,
} from './graphql/handleBagReshipScanEvent';

type Tab = 'success' | 'failure';

interface ScanRecord {
    id: number;
    bagNumber: string;
    sortingRule: string;
    reason?: string;
    time: string;
}

const formatTime = (date: Date): string => {
    const hh = date.getHours();
    const mm = date.getMinutes().toString().padStart(2, '0');
    const period = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    return `${h12}:${mm} ${period}`;
};

interface PageBodyProps {
    scanPrompt: ReturnType<typeof useScanResultPrompt>;
}

const PageBody = ({ scanPrompt }: PageBodyProps) => {
    const { data } = useQuery<GetBagReshipFlowOptionsResponse>(
        GET_BAG_RESHIP_FLOW_OPTIONS,
        { fetchPolicy: 'cache-and-network' },
    );
    const [handleBagReshipScanEvent] = useMutation<
        HandleBagReshipScanEventResponse,
        HandleBagReshipScanEventVariables
    >(HANDLE_BAG_RESHIP_SCAN_EVENT);

    const carrierList: ReshipCarrierType[] = data?.carrierWithFlowList ?? [];

    const [selectedCarrier, setSelectedCarrier] = useState<string | null>(null);
    const [selectedFlow, setSelectedFlow] = useState<string | null>(null);
    const [bagNumber, setBagNumber] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>('success');
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failureRecords, setFailureRecords] = useState<ScanRecord[]>([]);
    const [carrierPickerVisible, setCarrierPickerVisible] = useState(false);
    const [flowPickerVisible, setFlowPickerVisible] = useState(false);

    const scanResult = useScanResult();
    // 串行化扫描：mutation 未返回前拒绝新扫描，避免连扫并发
    const scanningRef = useRef(false);

    const currentCarrier = carrierList.find(c => c.code === selectedCarrier) ?? null;
    const flowList = currentCarrier?.flowList ?? [];
    const selectedSortingRule = flowList.find(f => f.code === selectedFlow)?.sortingRule ?? '';

    useEffect(() => {
        if (scanResult) {
            handleScan(scanResult);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanResult]);

    const handleScan = async (value: string) => {
        if (submitting || scanningRef.current) return;
        const bag = value.trim();
        if (!bag) return;
        setBagNumber('');
        if (!selectedCarrier || !selectedFlow) {
            // 该提示的 i18n 词条自带 title，不额外叠入口 title
            scanPrompt.showResult(buildClientError(ClientError.BAG_RESHIP_SELECT_REQUIRED), bag);
            return;
        }
        const sortingRule = selectedSortingRule;
        scanningRef.current = true;
        setSubmitting(true);
        scanPrompt.clearPrompt();
        try {
            const { data: mData } = await handleBagReshipScanEvent({
                variables: { bagNumber: bag, targetFlow: selectedFlow, lmCarrierCode: selectedCarrier },
            });
            const raw = mData?.handleBagReshipScanEvent;
            const envelope: ScanResultEnvelope<BagReshipScanResultType> | undefined = raw
                ? { success: raw.success, message: raw.message ?? null, data: raw }
                : undefined;
            if (raw?.success) {
                scanPrompt.showResult(envelope as ScanResultEnvelope, bag);
                // 成功记录必须显示服务端实际写入的分拣规则：配置串的末段可能带全称后缀（如 GOFATL），
                // 服务端写入/发给 TT 的是归一后的三位简码（GOF），照配置串抄会写错分拣码贴到包上
                setSuccessRecords(prev => [{
                    id: Date.now(),
                    bagNumber: bag,
                    sortingRule: raw.newSortingRule || sortingRule,
                    time: formatTime(new Date()),
                }, ...prev]);
                setActiveTab('success');
            } else if (envelope) {
                scanPrompt.showResult(envelope as ScanResultEnvelope, bag);
                playScanFailedAoSound();
                setFailureRecords(prev => [{
                    id: Date.now(),
                    bagNumber: bag,
                    sortingRule,
                    reason: getEnvelopeFailedReason(envelope) || t('pda.common.scanFailed'),
                    time: formatTime(new Date()),
                }, ...prev]);
                setActiveTab('failure');
            }
        } catch (e: any) {
            scanPrompt.showResult(buildClientError(ClientError.NETWORK_ERROR, {}, ClientError.BAG_RESHIP_SCAN_TITLE), bag);
            playScanFailedAoSound();
            setFailureRecords(prev => [{
                id: Date.now(),
                bagNumber: bag,
                sortingRule,
                reason: parseException(e).message,
                time: formatTime(new Date()),
            }, ...prev]);
            setActiveTab('failure');
        } finally {
            scanningRef.current = false;
            setSubmitting(false);
        }
    };

    const records = activeTab === 'success' ? successRecords : failureRecords;

    return (
        <div className="px-1 py-3">
            {/* 选择尾程商（下拉） */}
            <div
                className="flex items-center border rounded mb-2 cursor-pointer"
                onClick={() => setCarrierPickerVisible(true)}
            >
                <Input
                    className="flex-1 p-2"
                    placeholder={t('pda.pages.bagReship.selectCarrier')}
                    value={selectedCarrier ?? ''}
                    readOnly
                    style={{ cursor: 'pointer' }}
                />
                <div className="px-2"><DownOutline fontSize={18} /></div>
            </div>
            {/* 选择新流向（下拉） */}
            <div
                className="flex items-center border rounded mb-2 cursor-pointer"
                onClick={() => setFlowPickerVisible(true)}
            >
                <Input
                    className="flex-1 p-2"
                    placeholder={t('pda.pages.bagReship.selectFlow')}
                    value={selectedFlow ?? ''}
                    readOnly
                    style={{ cursor: 'pointer' }}
                />
                <div className="px-2"><DownOutline fontSize={18} /></div>
            </div>
            {/* 扫描待改流向包牌 */}
            <ScanInput
                placeholder={t('pda.pages.bagReship.placeholderScan')}
                value={bagNumber}
                onScan={handleScan}
                onChange={setBagNumber}
            />
            {/* 改为分拣码 */}
            <div className="bg-blue-50 rounded px-3 py-2 my-2 text-sm text-gray-900">
                {t('pda.pages.bagReship.changeToSortingCode')}: {selectedSortingRule}
            </div>
            {/* 成功/失败 tabs */}
            <div className="flex border-b border-gray-200">
                <button
                    type="button"
                    className={`flex-1 py-2 text-base ${activeTab === 'success' ? 'text-purple-600 border-b-2 border-purple-600 font-bold' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('success')}
                >
                    {t('pda.pages.bagReship.successTab')}({successRecords.length})
                </button>
                <button
                    type="button"
                    className={`flex-1 py-2 text-base ${activeTab === 'failure' ? 'text-purple-600 border-b-2 border-purple-600 font-bold' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('failure')}
                >
                    {t('pda.pages.bagReship.failureTab')}({failureRecords.length})
                </button>
            </div>
            {/* 列头 */}
            <div className="flex items-center gap-2 px-1 py-2 text-xs text-gray-500 border-b border-gray-100">
                <span className="flex-1">{t('pda.pages.bagReship.bagNoColumn')}</span>
                <span className="flex-shrink-0 w-32">{t('pda.pages.bagReship.newSortingCodeColumn')}</span>
                <span className="flex-shrink-0 w-16 text-right">{t('pda.pages.bagReship.timeColumn')}</span>
            </div>
            {/* 记录列表 */}
            {records.length === 0 ? (
                <div className="text-center text-gray-400 py-6 text-sm">{t('pda.pages.bagReship.noRecords')}</div>
            ) : (
                records.map(rec => (
                    <div key={rec.id} className="py-2 border-b border-gray-100 text-sm">
                        <div className="flex items-start gap-2">
                            <span className="flex-1 break-all">{rec.bagNumber}</span>
                            <span className="flex-shrink-0 w-32 break-all">{rec.sortingRule}</span>
                            <span className="flex-shrink-0 w-16 text-right text-gray-500">{rec.time}</span>
                        </div>
                        {activeTab === 'failure' && rec.reason && (
                            <div className="pl-1 mt-1 text-red-500 break-all text-xs">
                                {t('pda.pages.bagReship.reasonColumn')}: {rec.reason}
                            </div>
                        )}
                    </div>
                ))
            )}

            {/* 下拉选择器 */}
            <Picker
                columns={[carrierList.map(c => ({ label: c.code, value: c.code }))]}
                visible={carrierPickerVisible}
                onClose={() => setCarrierPickerVisible(false)}
                value={selectedCarrier ? [selectedCarrier] : []}
                title={t('pda.pages.bagReship.selectCarrier')}
                onConfirm={(v: PickerValue[]) => {
                    setSelectedCarrier((v[0] as string) ?? null);
                    setSelectedFlow(null); // 换尾程商清空已选流向
                }}
            />
            <Picker
                columns={[flowList.map(f => ({ label: f.code, value: f.code }))]}
                visible={flowPickerVisible}
                onClose={() => setFlowPickerVisible(false)}
                value={selectedFlow ? [selectedFlow] : []}
                title={t('pda.pages.bagReship.selectFlow')}
                onConfirm={(v: PickerValue[]) => setSelectedFlow((v[0] as string) ?? null)}
            />
        </div>
    );
};

export default function BagReshipScanPage() {
    const scanPrompt = useScanResultPrompt();
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2 relative">
            <ScanResultPrompt prompt={scanPrompt.prompt} barCode={scanPrompt.barCode} onClose={scanPrompt.clearPrompt} />
            <Header title={t('pda.pages.bagReship.title')} />
            <Pda />
            <PageBody scanPrompt={scanPrompt} />
        </div>
    );
}
